import {
  createBusinessEntityLocation,
  TUpdateLocationParams,
  TCreateBusinessEntityLocation,
  updateBusinessEntityLocation,
} from '@commenda/api';
import { useContentBasedStateOptions } from '@commenda/api/content-library';
import { throwApiError } from '@commenda/utils';
import {
  AddressInput,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, { MIXPANEL_SOURCE, MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import {
  addressSchema,
  AddressSchemaFields,
  defaultAddressSchema,
} from '@commenda/schema/address';
import {
  IAddress,
  ILocation,
  LocationTypeRevamp as LocationType,
  LocationTypeRevamp,
} from '@commenda/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNotification } from '../../../../../hooks/useNotification';

const createAddressModalSchema = (
  isMailingAddress,
  isMailingAddressAdded,
  isRegisteredAddressAdded
) => {
  return z.object({
    locationTypes: z.array(z.string()).optional(),
    registeredOfficeAddress:
      isMailingAddressAdded || isMailingAddress
        ? z
          .object({
            address: z.any().optional(),
          })
          .optional()
        : z.object({
          address: addressSchema,
        }),
    mailingAddress:
      isRegisteredAddressAdded || !isMailingAddress
        ? z
          .object({
            address: z.any().optional(),
          })
          .optional()
        : z.object({
          address: addressSchema,
        }),
  });
};

type AddRegisteredAddressModalFormFields = z.infer<
  ReturnType<typeof createAddressModalSchema>
>;

interface BusinessEntity {
  id: number;
  name: string;
  country: Country;
}

interface AddRegisteredAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  isMailingAddress?: boolean;
  isRegisteredAddressAdded?: boolean;
  isMailingAddressAdded?: boolean;
  registeredOfficeAddress:
  | (ILocation & { locationTypes: LocationTypeRevamp[] })
  | null;
  mailingAddress: (ILocation & { locationTypes: LocationType[] }) | null;
  businessEntity: BusinessEntity;
  onSuccess: () => void;
}

export const AddRegisteredAddressModal = ({
  isOpen,
  onClose,
  isMailingAddress = false,
  isRegisteredAddressAdded = false,
  isMailingAddressAdded = false,
  registeredOfficeAddress,
  mailingAddress,
  businessEntity,
  onSuccess,
}: AddRegisteredAddressModalProps) => {
  const isSaving = useToggle();
  const { setNotification } = useNotification();
  const [hasSameMailingAddress, setHasSameMailingAddress] = useState(false);
  const [hasSameRegisteredAddress, setHasSameRegisteredAddress] =
    useState(false);

  const getDefaultValues = () => ({
    registeredOfficeAddress: registeredOfficeAddress?.address
      ? {
        ...registeredOfficeAddress.address,
        country:
          registeredOfficeAddress.address.country || businessEntity.country,
      }
      : {
        address: defaultAddressSchema,
      },
    mailingAddress: mailingAddress?.address
      ? {
        ...mailingAddress.address,
        country: mailingAddress.address.country || businessEntity.country,
      }
      : {
        address: defaultAddressSchema,
      },
  });

  // Store original values to compare for changes
  const originalValues = useMemo(
    () => getDefaultValues(),
    [
      isMailingAddress,
      registeredOfficeAddress,
      mailingAddress,
      businessEntity.country,
    ]
  );

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    unregister,
    reset,
    clearErrors,
  } = useForm<AddRegisteredAddressModalFormFields>({
    resolver: zodResolver(
      createAddressModalSchema(
        isMailingAddress,
        isMailingAddressAdded,
        isRegisteredAddressAdded
      )
    ),
    mode: 'onChange',
    defaultValues: getDefaultValues(),
  });

  // Clear errors when switching between modes
  useEffect(() => {
    if (isMailingAddress) {
      clearErrors('registeredOfficeAddress');
    } else {
      clearErrors('mailingAddress');
    }
  }, [isMailingAddress, clearErrors]);

  // Handle form reset and initialization when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset(getDefaultValues());
      setHasSameMailingAddress(false);
      setHasSameRegisteredAddress(false);
      return;
    }

    // Set form values when modal opens
    if (isMailingAddress && mailingAddress?.address) {
      setValue('mailingAddress', {
        address: mailingAddress.address,
      });
      setValue('locationTypes', mailingAddress.locationTypes);
    }

    if (!isMailingAddress && registeredOfficeAddress?.address) {
      setValue('registeredOfficeAddress', {
        address: registeredOfficeAddress.address,
      });
      setValue('locationTypes', registeredOfficeAddress.locationTypes);
    }
  }, [
    isOpen,
    isMailingAddress,
    mailingAddress,
    registeredOfficeAddress,
    setValue,
    reset,
  ]);

  const createLocationData = (
    address: AddressSchemaFields,
    locationTypes: LocationType[]
  ) => ({
    address: {
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      addressLine3: address.addressLine3,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country as Country,
    },
    locationTypes,
  });

  const saveAddress = async (
    locationData: TCreateBusinessEntityLocation,
    errorMessage: string,
    locationId?: string
  ) => {
    const response = locationId
      ? await updateBusinessEntityLocation(
        businessEntity.id,
        locationId,
        locationData
      )
      : await createBusinessEntityLocation(locationData as TCreateBusinessEntityLocation, businessEntity.id);

    if (!response.ok) {
      // Surface the backend's real error (e.g. sales-tax validation) via the
      // onSave catch, which renders error.message in the toast.
      await throwApiError(response, errorMessage);
    }

    return response;
  };

  const onSave = async (data: AddRegisteredAddressModalFormFields) => {
    if (isSaving.value) return;

    isSaving.on();

    try {
      const isSameAddress = hasSameMailingAddress || hasSameRegisteredAddress;
      const shouldSaveRegistered = !isMailingAddress || isSameAddress;
      const shouldSaveMailing = isMailingAddress || isSameAddress;

      const processAddressUpdate = async (
        addressData:
          | AddRegisteredAddressModalFormFields['registeredOfficeAddress']
          | AddRegisteredAddressModalFormFields['mailingAddress'],
        existingAddress: ILocation,
        targetLocationType: LocationType,
        errorMessage: string
      ) => {
        if (!addressData?.address) return;

        const hasMultipleLocationTypes = data.locationTypes?.length > 1;

        // this is to handle the where user had added multiple location types before in old ui
        // we need to create a new location for the location type and update the existing location by removing the location type from the array
        if (hasMultipleLocationTypes) {
          const remainingTypes = data.locationTypes.filter(
            (type) => type !== targetLocationType
          );

          const existingLocationData = createLocationData(
            existingAddress?.address,
            remainingTypes as LocationTypeRevamp[]
          );
          await saveAddress(
            existingLocationData,
            errorMessage,
            existingAddress?.id
          );

          const newLocationData = createLocationData(addressData.address, [
            targetLocationType,
          ]);
          await saveAddress(newLocationData, errorMessage);
        } else {
          const locationData = createLocationData(addressData.address, [
            targetLocationType,
          ]);
          await saveAddress(locationData, errorMessage, existingAddress?.id);
        }
      };

      if (shouldSaveRegistered) {
        await processAddressUpdate(
          data.registeredOfficeAddress,
          registeredOfficeAddress,
          LocationType.REGISTERED_OFFICE,
          'Failed to save registered address'
        );
      }

      if (shouldSaveMailing) {
        await processAddressUpdate(
          data.mailingAddress,
          mailingAddress,
          LocationType.MAILING_ADDRESS,
          'Failed to save mailing address'
        );
      }

      setNotification({
        title: 'Success',
        message: 'Address information updated successfully',
        valid: true,
        show: true,
      });

      mixpanel.track('ENTITY_REGISTERED_ADDRESS_ADDED', {
        source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
        surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
        business_entity_id: businessEntity.id,
        address_type: isMailingAddress ? 'mailing' : 'registered_office',
        is_edit: Boolean(
          isMailingAddress ? mailingAddress : registeredOfficeAddress
        ),
      });

      reset();
      onSuccess();
      onClose();
    } catch (error) {
      setNotification({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to save address',
        valid: false,
        show: true,
      });
    } finally {
      isSaving.off();
    }
  };

  const handleSameAsRegisteredOffice = (checked: boolean) => {
    if (checked) {
      clearErrors('mailingAddress');
      unregister('mailingAddress');
      setValue('mailingAddress', {
        address: currentValues?.registeredOfficeAddress?.address,
      });
      setHasSameMailingAddress(true);
    } else {
      setValue('mailingAddress', {
        address: defaultAddressSchema,
      });
      !isMailingAddressAdded && setHasSameMailingAddress(false);
    }
  };

  const handleSameAsMailingAddress = (checked: boolean) => {
    if (checked) {
      clearErrors('registeredOfficeAddress');
      unregister('registeredOfficeAddress');
      setValue('registeredOfficeAddress', {
        address: currentValues?.mailingAddress?.address,
      });
      setHasSameRegisteredAddress(true);
    } else {
      setValue('registeredOfficeAddress', {
        address: defaultAddressSchema,
      });
      !isRegisteredAddressAdded && setHasSameRegisteredAddress(false);
    }
  };

  const hasRegisteredOfficeAddress = Boolean(registeredOfficeAddress);
  const hasMailingAddress = isMailingAddress ? Boolean(mailingAddress) : true;
  const currentValues = watch();

  const currentAddress = isMailingAddress
    ? currentValues.mailingAddress
    : currentValues.registeredOfficeAddress;

  // Backend-driven state options: the picker only offers subdivision codes the
  // sales-tax validator accepts. Empty list (or in-flight) hides the state field
  // rather than falling back to the country-state-city library.
  const currentAddressCountry = currentAddress?.address?.country;
  const contentStateOptions = useContentBasedStateOptions(
    businessEntity.id,
    currentAddressCountry
  );
  const addressStateOptions = useMemo(
    () =>
      currentAddressCountry
        ? contentStateOptions.map((option) => ({
          text: option.label,
          value: option.value,
          label: option.label,
        }))
        : undefined,
    [contentStateOptions, currentAddressCountry]
  );

  // Validate required fields for form submission
  const isFormValid = Boolean(
    currentAddress?.address?.country?.trim() &&
    currentAddress?.address?.addressLine1?.trim() &&
    currentAddress?.address?.city?.trim() &&
    currentAddress?.address?.state?.trim() &&
    currentAddress?.address?.postalCode?.trim()
  );

  // Check if form data has changed from original values
  const hasFormChanged = useMemo(() => {
    const currentFormData = {
      registeredOfficeAddress: {
        address: currentValues?.registeredOfficeAddress?.address,
      },
      mailingAddress: {
        address: currentValues?.mailingAddress?.address,
      },
    };

    // For new addresses (not editing existing ones), consider it changed if any required field is filled
    const isAddingNewAddress = isMailingAddress
      ? !hasMailingAddress
      : !hasRegisteredOfficeAddress;

    if (isAddingNewAddress) {
      const currentAddress = isMailingAddress
        ? currentFormData.mailingAddress.address
        : currentFormData.registeredOfficeAddress.address;

      // Check if any required field has been filled
      return Boolean(
        currentAddress?.country?.trim() ||
        currentAddress?.addressLine1?.trim() ||
        currentAddress?.city?.trim() ||
        currentAddress?.state?.trim() ||
        currentAddress?.postalCode?.trim()
      );
    }

    // For editing existing addresses, compare with original values
    const registeredChanged =
      JSON.stringify(currentFormData.registeredOfficeAddress.address) !==
      JSON.stringify(originalValues.registeredOfficeAddress);

    const mailingChanged =
      JSON.stringify(currentFormData.mailingAddress.address) !==
      JSON.stringify(originalValues.mailingAddress);

    if (isMailingAddress) {
      return mailingChanged || hasSameRegisteredAddress;
    } else {
      return registeredChanged || hasSameMailingAddress;
    }
  }, [
    currentValues,
    originalValues,
    hasSameMailingAddress,
    hasSameRegisteredAddress,
    isMailingAddress,
    hasMailingAddress,
    hasRegisteredOfficeAddress,
  ]);

  const getModalTitle = () => {
    if (isMailingAddress) {
      return hasMailingAddress ? 'Edit mailing address' : 'Add mailing address';
    }
    return hasRegisteredOfficeAddress
      ? 'Edit registered address'
      : 'Add registered address';
  };

  const getModalDescription = () => {
    if (isMailingAddress) {
      return hasMailingAddress
        ? 'Update the details for this business mailing address.'
        : 'Enter the address where official documents and government notices are sent. This may differ from your registered or operational address';
    }
    return hasRegisteredOfficeAddress
      ? 'Update the details for this business registered address.'
      : 'Enter the address of your registered office, as present on your Certificate of Incorporation';
  };

  const handleAddressChange = (address: IAddress) => {
    if (isMailingAddress) {
      setValue('mailingAddress', { address });
    } else {
      setValue('registeredOfficeAddress', { address });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="overflow-y-hidden" preventCloseOnOutsideClick>
        <form
          onSubmit={handleSubmit(onSave)}
          className="flex flex-col max-h-[70vh]"
        >
          <DialogHeader className="flex flex-col gap-xs items-start">
            <DialogTitle>{getModalTitle()}</DialogTitle>
            <DialogDescription className="text-text-secondary">
              {getModalDescription()}
            </DialogDescription>
          </DialogHeader>

          <div className="p-3xl max-h-[440px] overflow-y-auto flex flex-col gap-2xl">
            <div className="flex flex-col gap-2xl">
              <AddressInput
                address={currentAddress?.address as IAddress}
                onChange={handleAddressChange}
                className="gap-xl"
                stateOptions={addressStateOptions}
              />

              {/* Same as registered office checkbox */}
              {!isMailingAddress && !isMailingAddressAdded && (
                <div className="flex items-center gap-md">
                  <Checkbox
                    checked={hasSameMailingAddress}
                    id="hasSameMailingAddress"
                    onCheckedChange={handleSameAsRegisteredOffice}
                  />
                  <Label
                    htmlFor="hasSameMailingAddress"
                    className="cursor-pointer"
                  >
                    Entity mailing address is same as entity registered office
                    address
                  </Label>
                </div>
              )}

              {/* Same as mailing address checkbox */}
              {isMailingAddress && !isRegisteredAddressAdded && (
                <div className="flex items-center gap-md">
                  <Checkbox
                    checked={hasSameRegisteredAddress}
                    id="hasSameRegisteredAddress"
                    onCheckedChange={handleSameAsMailingAddress}
                  />
                  <Label
                    htmlFor="hasSameRegisteredAddress"
                    className="cursor-pointer"
                  >
                    Entity registered address is same as entity mailing address
                  </Label>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={!isFormValid || isSaving.value || !hasFormChanged}
            >
              {isSaving.value ? 'Saving' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
